import { useEffect, useMemo, useState } from "react";
import { X, Plus, Minus, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

/**
 * CustomizeModal — opens when a customer taps + on a customizable menu item.
 * Lets them:
 *  - Remove individual ingredients (parsed from the description, comma-separated)
 *  - Add extras from the live Add-Ons menu category (each with its own price)
 *
 * Emits an add() to CartContext with removals + addOns. Nothing about which
 * items are customizable is hardcoded here: it's driven by the description
 * (admin-editable) and the Add-Ons menu items (admin-editable).
 */
export default function CustomizeModal({ item, onClose, onAdd }) {
  const { add } = useCart();
  const [removals, setRemovals] = useState(new Set());
  const [selectedAddOns, setSelectedAddOns] = useState({}); // id → true
  const [addOns, setAddOns] = useState([]);

  // Parse the description into a clean list of ingredients
  const ingredients = useMemo(() => {
    if (!item?.description) return [];
    return item.description
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [item]);

  useEffect(() => {
    api
      .get("/menu")
      .then((r) => {
        const active = r.data.filter(
          (m) => m.category === "Add-Ons" && m.available
        );
        // Filter by this item's `allowed_extras` whitelist when defined.
        // `null` / `undefined` => allow all (backward-compatible default).
        // `[]` => admin explicitly disabled all add-ons for this item.
        const allowed = item?.allowed_extras;
        const filtered = Array.isArray(allowed)
          ? active.filter((a) => new Set(allowed).has(a.id))
          : active;
        // Apply per-item price overrides (a map of addon_id -> price).
        const overrides = item?.extras_price_overrides || {};
        setAddOns(
          filtered.map((a) =>
            overrides[a.id] !== undefined && overrides[a.id] !== null
              ? { ...a, price: Number(overrides[a.id]) }
              : a
          )
        );
      })
      .catch(() => setAddOns([]));
  }, [item]);

  const toggleRemoval = (ing) => {
    setRemovals((cur) => {
      const next = new Set(cur);
      if (next.has(ing)) next.delete(ing);
      else next.add(ing);
      return next;
    });
  };

  const toggleAddOn = (a) => {
    setSelectedAddOns((cur) => ({ ...cur, [a.id]: !cur[a.id] }));
  };

  const chosenAddOns = addOns.filter((a) => selectedAddOns[a.id]);
  const addOnTotal = chosenAddOns.reduce((s, a) => s + Number(a.price || 0), 0);
  const total = Math.round((item.price + addOnTotal) * 100) / 100;

  const submit = () => {
    const payload = {
      removals: Array.from(removals),
      addOns: chosenAddOns.map((a) => ({ name: a.name, price: a.price })),
      total,
    };
    if (onAdd) {
      // POS mode — parent decides what to do with the customization.
      onAdd(item, payload);
    } else {
      add(item, {
        removals: payload.removals,
        addOns: payload.addOns,
      });
      toast.success(`${item.name} added`, { duration: 1400 });
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="customize-modal"
    >
      <div
        className="bg-white w-full sm:max-w-md max-h-[90dvh] rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="chalkboard-band px-4 py-3 flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-brand-yellow/90 font-black">
              Customize
            </div>
            <div className="font-display text-white text-2xl leading-tight truncate">
              {item.name}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="customize-close-btn"
            className="text-white/80 hover:text-white p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {ingredients.length > 0 && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black mb-2">
                Comes with — tap to remove
              </div>
              <div className="flex flex-wrap gap-2">
                {ingredients.map((ing) => {
                  const removed = removals.has(ing);
                  return (
                    <button
                      key={ing}
                      type="button"
                      onClick={() => toggleRemoval(ing)}
                      data-testid={`customize-ingredient-${ing.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold transition-colors ${
                        removed
                          ? "border-brand-red bg-red-50 text-brand-red line-through"
                          : "border-stone-300 bg-white text-stone-700 hover:border-brand-red"
                      }`}
                    >
                      {removed ? `NO ${ing.toUpperCase()}` : ing}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {addOns.length > 0 && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black mb-2">
                Add extras
              </div>
              <div className="space-y-2">
                {addOns.map((a) => {
                  const active = !!selectedAddOns[a.id];
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggleAddOn(a)}
                      data-testid={`customize-addon-${a.id}`}
                      className={`w-full text-left rounded-lg px-3 py-2.5 border-2 flex items-center gap-3 transition-colors ${
                        active
                          ? "border-emerald-600 bg-emerald-50"
                          : "border-stone-200 bg-white hover:border-emerald-400"
                      }`}
                    >
                      <div
                        className={`w-6 h-6 rounded-full border-2 shrink-0 flex items-center justify-center ${
                          active ? "border-emerald-600 bg-emerald-600" : "border-stone-300"
                        }`}
                      >
                        {active ? <Check className="w-3.5 h-3.5 text-white" /> : <Plus className="w-3.5 h-3.5 text-stone-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-stone-800 text-sm truncate">
                          {a.name}
                        </div>
                        {a.description && (
                          <div className="text-[11px] text-stone-500 truncate">
                            {a.description}
                          </div>
                        )}
                      </div>
                      <div className="font-display text-brand-red text-base shrink-0">
                        +${a.price.toFixed(2)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {ingredients.length === 0 && addOns.length === 0 && (
            <div className="text-center text-sm text-stone-500 py-6">
              No customizations available for this item.
            </div>
          )}
        </div>

        <footer className="border-t border-stone-200 px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
              Total
            </div>
            <div className="font-display text-brand-red text-2xl leading-none">
              ${total.toFixed(2)}
            </div>
          </div>
          <button
            onClick={submit}
            data-testid="customize-add-btn"
            className="bg-brand-red text-white px-6 py-3 rounded-full font-black uppercase tracking-widest text-xs hover:bg-red-800 transition-colors active:scale-95"
          >
            Add to Cart
          </button>
        </footer>
      </div>
    </div>
  );
}
